import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from '@tremor/react';
import { marked } from 'marked';
import { TOURS } from '../lib/tours';
import { resetTourSeen } from './Tour';
import { t } from '../lib/i18n';

// Vite ?raw imports bundle each help file as a TS string at build time. Add
// new pages here and in routeToHelpKey() — no runtime fetch, no Go-side
// route, no rebuild needed for content updates beyond bundling.
import defaultMd     from '../help/_default.md?raw';
import overviewMd    from '../help/overview.md?raw';
import salesMd       from '../help/sales.md?raw';
import partsMd       from '../help/parts.md?raw';
import arMd          from '../help/ar.md?raw';
import inventoryMd   from '../help/inventory.md?raw';
import vendorsMd     from '../help/vendors.md?raw';
import salespeopleMd from '../help/salespeople.md?raw';
import lostMd        from '../help/lost.md?raw';
import serviceMd     from '../help/service.md?raw';
import estadoMd      from '../help/estado.md?raw';
import pagosMd       from '../help/pagos.md?raw';
import reportsMd     from '../help/reports.md?raw';
import archivesMd    from '../help/archives.md?raw';
import customerMd    from '../help/customer.md?raw';
import partMd        from '../help/part.md?raw';
import purchasingMd  from '../help/purchasing.md?raw';
import customersListMd from '../help/customersList.md?raw';
import adminMd       from '../help/admin.md?raw';
import configMd      from '../help/config.md?raw';

const HELP: Record<string, string> = {
  default:     defaultMd,
  overview:    overviewMd,
  sales:       salesMd,
  parts:       partsMd,
  ar:          arMd,
  inventory:   inventoryMd,
  vendors:     vendorsMd,
  salespeople: salespeopleMd,
  lost:        lostMd,
  service:     serviceMd,
  estado:      estadoMd,
  pagos:       pagosMd,
  reports:     reportsMd,
  archives:    archivesMd,
  customer:    customerMd,
  part:        partMd,
  purchasing:  purchasingMd,
  customersList: customersListMd,
  admin:       adminMd,
  config:      configMd,
};

// Map a router pathname to one of the keys above. Generic routes that take
// path params (/customers/:anum, /parts/:line/:part) go to the topic, not
// the listing.
function routeToHelpKey(pathname: string): string {
  if (pathname === '/' || pathname === '') return 'overview';
  // Order matters — /customers/123 should hit "customer", not the list.
  if (pathname.startsWith('/customers/'))   return 'customer';
  if (pathname === '/customers')            return 'customersList';
  if (pathname.startsWith('/purchasing'))   return 'purchasing';
  if (pathname.startsWith('/admin/config')) return 'config';
  if (pathname.startsWith('/admin'))        return 'admin';
  if (pathname.match(/^\/parts\/[^/]+\/[^/]+/)) return 'part';
  if (pathname.startsWith('/sales'))        return 'sales';
  if (pathname.startsWith('/parts'))        return 'parts';
  if (pathname.startsWith('/inventory'))    return 'inventory';
  if (pathname.startsWith('/ar'))           return 'ar';
  if (pathname.startsWith('/vendors'))      return 'vendors';
  if (pathname.startsWith('/vendedores'))   return 'salespeople';
  if (pathname.startsWith('/lost'))         return 'lost';
  if (pathname.startsWith('/service'))      return 'service';
  if (pathname.startsWith('/estado'))       return 'estado';
  if (pathname.startsWith('/pagos'))        return 'pagos';
  if (pathname.startsWith('/reports'))      return 'reports';
  if (pathname.startsWith('/archives'))     return 'archives';
  return 'default';
}

/**
 * Renders the help Markdown for the current route. The content is bundled
 * at build time (Vite ?raw); marked.parse turns it into HTML and we drop
 * it via dangerouslySetInnerHTML. Safe because the source is our own files
 * — no user input ever enters this pipeline.
 *
 * Tailwind's `prose` classes give the markup readable typography without
 * us having to style each tag. The `prose-sm prose-tremor` variants tuck
 * it into the drawer's narrower column.
 */
export default function HelpPanel() {
  const loc = useLocation();
  const key = routeToHelpKey(loc.pathname);
  const html = useMemo(() => {
    const src = HELP[key] ?? HELP.default;
    return marked.parse(src, { async: false }) as string;
  }, [key]);

  // Only show the "Mostrar guía" button when the current page actually has
  // a tour script registered. Pages without one would have a button that
  // does nothing — better to hide it.
  const tour = TOURS[key];
  function relaunchTour() {
    if (!tour) return;
    resetTourSeen(tour.key);
    // Fire a global event the page listens to. The drawer closes itself
    // via its parent SupportButton state; we don't have access to that
    // here, but the page tour will mount on top of the drawer's backdrop.
    window.dispatchEvent(new Event('pacebi:open-tour'));
  }

  return (
    <div className="space-y-3">
    {tour && (
      <Button size="xs" variant="secondary" onClick={relaunchTour}>
        ▶ {t.tour.startButton}
      </Button>
    )}
    <div
      // The Tremor preset doesn't ship a typography plugin, so set spacing
      // explicitly. Headings get color/weight from Tremor variables to stay
      // on-brand.
      className="text-sm space-y-2
        [&_h1]:text-xl [&_h1]:font-semibold [&_h1]:text-tremor-content-strong [&_h1]:mt-0 [&_h1]:mb-2
        [&_h2]:text-base [&_h2]:font-semibold [&_h2]:text-tremor-content-strong [&_h2]:mt-4 [&_h2]:mb-1
        [&_h3]:text-sm  [&_h3]:font-semibold [&_h3]:text-tremor-content-strong [&_h3]:mt-3 [&_h3]:mb-1
        [&_p]:text-tremor-content [&_p]:my-2
        [&_ul]:list-disc [&_ul]:ml-5 [&_ul]:my-2 [&_ul_li]:text-tremor-content
        [&_ol]:list-decimal [&_ol]:ml-5 [&_ol]:my-2 [&_ol_li]:text-tremor-content
        [&_li]:my-0.5
        [&_code]:bg-tremor-background-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:rounded [&_code]:text-xs [&_code]:font-mono
        [&_table]:w-full [&_table]:my-2 [&_table]:text-xs
        [&_th]:text-left [&_th]:font-medium [&_th]:border-b [&_th]:border-tremor-border [&_th]:py-1
        [&_td]:py-1 [&_td]:border-b [&_td]:border-tremor-border
        [&_strong]:text-tremor-content-strong [&_strong]:font-semibold"
      dangerouslySetInnerHTML={{ __html: html }}
    />
    </div>
  );
}
